import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {SharedSettings} from '@shared/models/shared-settings';
import {
    OutboundLoad,
    OutboundLoadLineItem,
    OutboundLoadPartial,
    OutboundLoadCreate
} from '../models/outbound-load';
import {SearchResult} from '@shared/models/search-result';
import {DateTime} from 'luxon';
import {url} from '@shared/helpers/url';
import {map, tap} from 'rxjs/operators';
import {OutboundLoadPartialUpdate} from '../models/outbound-load-partial-update';
import {SequenceType} from '../models/sequence-type';
import {HistoryService} from '@shared/services/history.service';
import {HistoryResponse} from "@shared/models/history";
import {BulkCreateResult} from "../models/generic/bulk-create-result";

@Injectable({
    providedIn: 'root'
})
export class OutboundLoadService {

    constructor(private http: HttpClient,
                private settings: SharedSettings,
                private historyService: HistoryService) { }

    getLoadDetail(id: number, fullLoad = false): Observable<OutboundLoad> {
        let fulloadString = fullLoad ? `?fullLoad=true` : ''
        return this.http.get<OutboundLoad>(this.settings.apiBaseUrl + url`outboundLoads/${id}` + fulloadString).pipe(map(this.parseOutboundLoadDates));
    }

    getOutboundLoads(siteId: string, date: DateTime, shiftId: number): Observable<OutboundLoad[]> {
        if (siteId && date && shiftId) {
            return this.http.get<OutboundLoad[]>(this.settings.apiBaseUrl + url`OutboundLoads?siteId=${siteId}&date=${date.toISODate()}&shiftId=${shiftId}`).pipe(map(loads => loads.map(l => this.parseOutboundLoadDates(l))));
        }
        else
            return of([]);
    }

    searchOutboundLoads(request: any): Observable<SearchResult<OutboundLoad>> {
        return this.http.get<SearchResult<OutboundLoad>>(this.settings.apiBaseUrl + 'OutboundLoads/search', { params: request }).pipe(
          tap(res => {
              res.resultSet = res.resultSet.map(l => this.parseOutboundLoadDates(l));
          })
        );
    }

    refNumberInUse(customerId: number, refNumber: string): Observable<number> {
        return this.http.get<number>(this.settings.apiBaseUrl + url`OutboundLoads/RefNumInUse/${customerId}/${refNumber.trim()}`);
    }

    getProductsTotalQuantity(outboundLoadLineItems: OutboundLoadLineItem[]): number {
        let getProductsTotalQuantity = null;
        outboundLoadLineItems.forEach(outboundLoadLineItem => {
            getProductsTotalQuantity += outboundLoadLineItem.quantity;
        });

        return getProductsTotalQuantity;
    }

    getNextBolNumber(siteId: string): Observable<string> {
        return this.http.get(this.settings.apiBaseUrl + url`sites/${siteId}/sequences/${SequenceType.OutboundBolNumber}`, { responseType: 'text' });
    }

    createOutboundLoad(load: OutboundLoad): Observable<OutboundLoad> {
        return this.http.post<OutboundLoad>(this.settings.apiBaseUrl + url`OutboundLoads?updateDate=${DateTime.local().toISODate()}`, load)
            .pipe(map(this.parseOutboundLoadDates));
    }

    updateOutboundLoad(load: OutboundLoad): Observable<OutboundLoad> {
        return this.http.put<OutboundLoad>(this.settings.apiBaseUrl + url`OutboundLoads?updateDate=${DateTime.local().toISODate()}`, load)
            .pipe(map(this.parseOutboundLoadDates));
    }

    deleteOutboundLoad(loadId: number) {
        return this.http.delete<OutboundLoad>(this.settings.apiBaseUrl + `OutboundLoads/${loadId}`);
    }

    loadGridUpdate(update: OutboundLoadPartialUpdate): Observable<OutboundLoadPartial> {
        const { shippedWorkedShift: workedShift, ...cleaned } = update;
        return this.http.patch<OutboundLoadPartial>(
            this.settings.apiBaseUrl + url`OutboundLoads/${update.id}/partialUpdate?updateDate=${DateTime.local().toISODate()}`, cleaned)
            .pipe(map(this.parseOutboundLoadDates));
    }

    markLoadCanceled(loadId: number, canceledWorkedShiftId: number): Observable<any> {
        return this.http.post(
            this.settings.apiBaseUrl + url`OutboundLoads/${loadId}/cancel`, canceledWorkedShiftId);
    }

    resendNotification(loadId: number): Observable<any> {
        return this.http.post(
          this.settings.apiBaseUrl + url`OutboundLoads/${loadId}/resendNotification`, {});
    }

    getHistory(siteId: string, id: number): Observable<HistoryResponse[]> {
        return this.historyService.getOutboundLoadHistory(siteId, id);
    }

    replaceBol(selectedLoad: number, replacementLoad: number) {
        return this.http.post(this.settings.apiBaseUrl + url`OutboundLoads/${selectedLoad}/replaceImportedLoad/${replacementLoad}`, {});
    }

    bulkCreate(outboundLoads: OutboundLoadCreate[]): Observable<BulkCreateResult<OutboundLoad, string>[]> {
        return this.http.post<BulkCreateResult<OutboundLoad, string>[]>(this.settings.apiBaseUrl + url`OutboundLoads/bulkCreate`, outboundLoads);
    }

    parseOutboundLoadDates(load: OutboundLoad): OutboundLoad {
        load.loadedTimestamp = load.loadedTimestamp ? DateTime.fromISO(<any>load.loadedTimestamp) : null;
        load.shippedTimestamp = load.shippedTimestamp ? DateTime.fromISO(<any>load.shippedTimestamp) : null;
        load.customerNeededTimestamp = load.customerNeededTimestamp ? DateTime.fromISO(<any> load.customerNeededTimestamp) : null;
        load.scheduledPickupTimestamp = load.scheduledPickupTimestamp ? DateTime.fromISO(<any> load.scheduledPickupTimestamp) : null;
        load.loadedNotificationTS = load.loadedNotificationTS ? DateTime.fromISO(<any>load.loadedNotificationTS) : null;

        if (load.loadedWorkedShift)
            load.loadedWorkedShift.date = DateTime.fromISO(<any>load.loadedWorkedShift.date)
        if (load.shippedWorkedShift)
            load.shippedWorkedShift.date = DateTime.fromISO(<any>load.shippedWorkedShift.date)
        if (load.canceledWorkedShift)
            load.canceledWorkedShift.date = DateTime.fromISO(<any>load.canceledWorkedShift.date)

        if (load.firstClosedShift && load.firstClosedShift.date)
            load.firstClosedShift.date = DateTime.fromISO(<any> load.firstClosedShift.date);

        return load;
    }
}
